// 공통 사이트 헤더 — 메인 · 검사 안내 · 내비두 소개 · 연구보고서 (2026-10-06 HomePage 에서 분리 · R06-01)
// 2026-11-01 mid=461: 회원·지원기관 화면도 이 헤더 사용 · 지금 보고 있는 메뉴 표시 · 설문목록은 로그인 후만
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@shared/lib/auth/useAuth';
import { SURVEY_KIND_META, parseSurveyKindParam, surveyKindParam, type SurveyKind } from '@entities/survey-kind';
import { NaviBrand } from '@shared/ui/NaviBrand';
import s from './SiteHeader.module.scss';

export type SiteMenu = 'intro' | 'main' | 'guide' | 'about' | 'research' | 'rounds'
  | 'agency-participant' | 'agency-voucher' | 'agency-mypage' | 'mypage' | null;

export const SITE_MENUS: Array<{ key: Exclude<SiteMenu, null>; label: string; to: string }> = [
  { key: 'intro', label: '인트로', to: '/' }, // 2026-10-21 · 인트로 = 첫 화면
  { key: 'main', label: '메인', to: '/main' }, // 2026-11-01 mid=461 · 회원 화면도 이 헤더 → '메인으로' 버튼 대신
  { key: 'guide', label: '검사 안내', to: '/guide' },
  { key: 'about', label: '내비두 소개', to: '/about' },
  { key: 'research', label: '연구보고서', to: '/research' },
];

/** 로그인 후 메뉴 — 회원 = 설문목록 · 지원기관 담당자 = 참여자 관리·이용권관리·나의 페이지 */
const MEMBER_MENUS: Array<{ key: Exclude<SiteMenu, null>; label: string; to: string; id: string }> = [
  { key: 'rounds', label: '설문목록', to: '/round', id: 'rounds' },
];
const MANAGER_MENUS: Array<{ key: Exclude<SiteMenu, null>; label: string; to: string; id: string }> = [
  { key: 'agency-participant', label: '참여자 관리', to: '/agency/participant', id: 'agency-participant' },
  { key: 'agency-mypage', label: '나의 페이지', to: '/agency/mypage', id: 'agency-mypage' },
];

/** 설문목록 2뎁스 — 전체 · 간편 · 종합 · 정기 · 일경험 (2026-11-01 mid=464 · 기본 전체) */
const ROUND_KINDS: Array<[SurveyKind, string]> = [['QUICK', '간편'], ['COMPREHENSIVE', '종합'], ['PERIODIC', '정기'], ['WORK_EVAL', '일경험']];

/** 주소로 지금 보고 있는 메뉴 판단 (화면에서 active 를 넘기지 않을 때). */
export function menuOfPath(pathname: string): SiteMenu {
  const under = (p: string) => pathname === p || pathname.startsWith(`${p}/`);
  if (pathname === '/') return 'intro';
  if (under('/main')) return 'main';
  if (under('/guide')) return 'guide';
  if (under('/about')) return 'about';
  if (under('/research')) return 'research';
  if (under('/round')) return 'rounds';
  if (under('/agency/participant')) return 'agency-participant';
  if (under('/agency/voucher')) return 'agency-voucher';
  if (under('/agency/mypage') || under('/agency/report')) return 'agency-mypage';
  if (under('/mypage')) return 'mypage';
  return null;
}

interface SubItem { id: string; label: string; to: string; on: boolean; aria?: string }

/**
 * 2뎁스 메뉴 — 헤더 아래 한 줄 (지금 항목 표시).
 *   * 회원 설문목록(/round): 전체 · 간편 · 종합 · 정기 · 일경험 (기본 전체 · mid=464)
 *   * 지원기관 나의 페이지: 기관검사 현황(/agency/mypage · 기본) · 이용권 관리(mid=479) · 마이페이지(/mypage) (mid=476)
 *   * 지원기관 참여자 관리: 참여자 리스트(기본) · 일경험수행평가 참여(?view=work-eval) (mid=476)
 */
export function subMenuOf(pathname: string, search: string, userType: string | null): { id: string; label: string; items: SubItem[] } | null {
  const q = new URLSearchParams(search);
  if (userType === 'MANAGER') {
    if (pathname === '/agency/mypage' || pathname.startsWith('/agency/report/') || pathname === '/mypage' || pathname === '/agency/voucher') {
      return { id: 'nav-agency-mypage', label: '나의 페이지', items: [
        { id: 'sub-agency-status', label: '기관검사 현황', to: '/agency/mypage', on: pathname === '/agency/mypage' || pathname.startsWith('/agency/report/') },
        // 이용권 관리도 나의 페이지 아래 (2026-11-01 mid=479 · 상단 메뉴에서 이동)
        { id: 'sub-agency-voucher', label: '이용권 관리', to: '/agency/voucher', on: pathname === '/agency/voucher' },
        { id: 'sub-agency-mypage', label: '마이페이지', to: '/mypage', on: pathname === '/mypage' },
      ] };
    }
    if (pathname === '/agency/participant') {
      const work = q.get('view') === 'work-eval';
      return { id: 'nav-agency-participant', label: '참여자 관리', items: [
        { id: 'sub-participant-list', label: '참여자 리스트', to: '/agency/participant', on: !work },
        { id: 'sub-participant-work-eval', label: '일경험수행평가 참여', to: '/agency/participant?view=work-eval', on: work },
      ] };
    }
    return null;
  }
  if (pathname !== '/round') return null;
  const kind = parseSurveyKindParam(q.get('kind'));
  return { id: 'nav-round-kinds', label: '설문 종류', items: [
    { id: 'sub-round-all', label: '전체', to: '/round', on: kind === null },
    ...ROUND_KINDS.map(([k, label]) => ({ id: `sub-round-${surveyKindParam(k)}`, label, to: `/round?kind=${surveyKindParam(k)}`,
      on: kind === k, aria: SURVEY_KIND_META[k].label })),
  ] };
}

export function SiteHeader({ active }: { active?: SiteMenu }) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const member = useAuth((st) => st.member);
  const logout = useAuth((st) => st.logout);
  const isAuthed = Boolean(member);
  const path = active === undefined ? menuOfPath(pathname) : active;
  // 지원기관 마이페이지·이용권 관리는 '나의 페이지' 아래 (mid=476 · mid=479)
  const current = member?.userType === 'MANAGER' && (path === 'mypage' || path === 'agency-voucher') ? 'agency-mypage' : path;
  const roleMenus = !member ? [] : member.userType === 'MANAGER' ? MANAGER_MENUS : MEMBER_MENUS;
  const on = (key: SiteMenu) => ({
    className: current === key ? s.gnbOn : undefined,
    'aria-current': current === key ? ('page' as const) : undefined,
  });

  const subMenu = subMenuOf(pathname, search, member?.userType ?? null);

  // PC 메뉴(gnb-*) · 모바일 메뉴(gnb-m-*) 가 같은 링크를 두 번 그림 → id 접두사로 구분
  const links = (prefix: string) => (
    <>
      {SITE_MENUS.map((m) => (
        <Link key={m.key} id={`${prefix}-${m.key}`} to={m.to} {...on(m.key)}>{m.label}</Link>
      ))}
      {/* 설문목록은 로그인 후에만 (2026-11-01 mid=461) */}
      {roleMenus.map((m) => (
        <Link key={m.key} id={`${prefix}-${m.id}`} to={m.to} {...on(m.key)}>{m.label}</Link>
      ))}
      {!isAuthed && <Link id={`${prefix}-quick-test`} to="/quick-test">간편검사</Link>}
    </>
  );

  return (
    <header className={s.hdr} role="banner">
      <div className={s.hdrIn}>
        <NaviBrand />
        <nav className={s.gnb} aria-label="주요 메뉴">{links('gnb')}</nav>
        <div id="home-header-actions" className={s.hdrAct}>
          {isAuthed ? (
            <>
              {member?.nickName && (
                <span id="home-header-greet" className={s.greet}><strong>{member.nickName}</strong>님</span>
              )}
              {member?.userType !== 'MANAGER' && <button id="btn-home-mypage" type="button"
                className={current === 'mypage' ? `${s.btnText} ${s.btnOn}` : s.btnText}
                aria-current={current === 'mypage' ? 'page' : undefined}
                onClick={() => navigate('/mypage')}>마이페이지</button>}
              <button id="btn-home-logout" type="button" className={s.btnSolid} onClick={() => logout()}>로그아웃</button>
            </>
          ) : (
            <>
              <button id="btn-home-login" type="button" className={s.btnText} onClick={() => navigate('/login')}>로그인</button>
              <button id="btn-home-signup" type="button" className={s.btnSolid} onClick={() => navigate('/signup')}>회원가입</button>
            </>
          )}
        </div>
      </div>
      <nav className={s.gnbMobile} aria-label="주요 메뉴 (모바일)">{links('gnb-m')}</nav>
      {subMenu && (
        <nav id={subMenu.id} className={s.subNav} aria-label={subMenu.label}>
          <div className={s.subIn}>
            {subMenu.items.map((it) => (
              <Link key={it.id} id={it.id} to={it.to} aria-label={it.aria}
                className={it.on ? `${s.subLink} ${s.subOn}` : s.subLink}
                aria-current={it.on ? 'page' : undefined}>{it.label}</Link>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}
